import { FC, memo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import { useResponsive } from '../../../../hooks';
import { Text } from '../../components-v4';
import { Block } from '../../pages/pages.styled';
import { StatusSubWrapper, StatusWrapper } from './request.styled';
import { Status } from '../status';
import { Files } from '../files';
import { Details } from '../details';
import { Info } from '../info';
import { RequestSkeleton } from '../skeletons';
import {
  getPublicRequestData,
  getOnLoadPublicRequestState,
} from '../../selectors';
import { MIN_SKELETON_DURATION } from '../../constants';

export const Request: FC = memo(() => {
  const { viewport } = useResponsive();
  const requestData = useSelector(getPublicRequestData);
  const onLoadRequest = useSelector(getOnLoadPublicRequestState);
  const [showSkeleton, setShowSkeleton] = useState<boolean>(onLoadRequest);
  const loadStartRef = useRef<number | null>(onLoadRequest ? Date.now() : null);

  useEffect(() => {
    if (onLoadRequest) {
      loadStartRef.current = Date.now();
      setShowSkeleton(true);
    } else if (loadStartRef.current !== null) {
      const elapsed = Date.now() - loadStartRef.current;
      const remaining = MIN_SKELETON_DURATION - elapsed;

      if (remaining > 0) {
        const timer = setTimeout(() => setShowSkeleton(false), remaining);
        return () => clearTimeout(timer);
      }

      setShowSkeleton(false);
    }
  }, [onLoadRequest]);

  if (showSkeleton) {
    return <RequestSkeleton />;
  }

  return (
    <Block id="request">
      <Text variant="heading-3" color="gray.800">
        Заявка от{' '}
        {!!requestData.created_at &&
          moment(requestData.created_at).format('DD.MM.YYYY/HH:mm')}
      </Text>
      <StatusWrapper>
        <Text
          variant={viewport.mobile ? 'heading-5' : 'heading-4'}
          color="gray.500"
        >
          Статус
        </Text>
        <StatusSubWrapper>
          <Status
            status={requestData.status}
            statusRu={requestData.status_ru}
          />
          {requestData.status === 'delayed' && !!requestData.transfer_date && (
            <Text variant="body-16" color="gray.800">
              до {moment(requestData.transfer_date).format('DD.MM.YYYY')}
            </Text>
          )}
        </StatusSubWrapper>
      </StatusWrapper>
      <Info requestData={requestData} />
      <Files
        canAdd={['accepted', 'run', 'delayed'].includes(requestData.status)}
      />
      <Details />
    </Block>
  );
});
